/**
 * Frontend constants for Wyden expense types
 *
 * Mirrors the backend constants for use in frontend components.
 * These constants define the expense type categories, their account numbers,
 * and help text for the expense type selector component.
 */

export interface WydenExpenseTypeDefinition {
  code: number; // Numeric code for accounting exports (6500, 6510, etc.)
  name: string;
  accountNumber: string;
  description: string;
  examples: string[];
}

// Keyed by string (EDUCATION, CATERING, etc.) as single source of truth
export const WYDEN_EXPENSE_TYPES: Record<string, WydenExpenseTypeDefinition> = {
  EDUCATION: {
    code: 1,
    name: 'Education',
    accountNumber: '6500',
    description: 'Training, conferences, courses, and professional development',
    examples: ['Conference registration', 'Online course', 'Certification exam', 'Workshop attendance']
  },
  CATERING: {
    code: 2,
    name: 'Catering',
    accountNumber: '6510',
    description: 'Business meals and client entertainment',
    examples: ['Client dinner', 'Team lunch', 'Working meeting meals', 'Business breakfast']
  },
  REPRESENTATION: {
    code: 4,
    name: 'Representation',
    accountNumber: '6520',
    description: 'Client gifts and business hospitality',
    examples: ['Client gifts', 'Promotional items', 'Business event hosting']
  },
  TRAVEL: {
    code: 6,
    name: 'Travel',
    accountNumber: '6530',
    description: 'Business travel expenses',
    examples: ['Flights', 'Hotel stays', 'Train tickets', 'Taxi/Uber', 'Parking', 'Tolls']
  },
  OTHER: {
    code: 10,
    name: 'Other',
    accountNumber: '6590',
    description: 'Miscellaneous business expenses',
    examples: ['Postage', 'Shipping', 'Subscriptions', 'Memberships']
  },
  OFFICE_MATERIAL: {
    code: 11,
    name: 'Office material',
    accountNumber: '6540',
    description: 'Office supplies and small equipment',
    examples: ['Stationery', 'Printer supplies', 'Desk accessories', 'USB cables']
  }
};

export type WydenExpenseType = keyof typeof WYDEN_EXPENSE_TYPES;

export const VALID_EXPENSE_TYPES: WydenExpenseType[] = ['EDUCATION', 'CATERING', 'REPRESENTATION', 'TRAVEL', 'OTHER', 'OFFICE_MATERIAL'];

// Legacy mapping for migration from numeric codes
export const EXPENSE_TYPE_CODE_TO_STRING: Record<number, WydenExpenseType> = {
  1: 'EDUCATION',
  2: 'CATERING',
  4: 'REPRESENTATION',
  6: 'TRAVEL',
  10: 'OTHER',
  11: 'OFFICE_MATERIAL'
};

/**
 * Get expense type definition by string
 */
export function getExpenseTypeByString(type: string): WydenExpenseTypeDefinition | undefined {
  return WYDEN_EXPENSE_TYPES[type];
}

/**
 * Get expense type definition by code (legacy support)
 */
export function getExpenseTypeByCode(code: number): WydenExpenseTypeDefinition | undefined {
  const typeString = EXPENSE_TYPE_CODE_TO_STRING[code];
  return typeString ? WYDEN_EXPENSE_TYPES[typeString] : undefined;
}

/**
 * Get account number by expense type string
 */
export function getAccountNumber(type: string): string | undefined {
  return WYDEN_EXPENSE_TYPES[type]?.accountNumber;
}

/**
 * Check if expense type string is valid
 */
export function isValidExpenseType(type: string): boolean {
  return type in WYDEN_EXPENSE_TYPES;
}

/**
 * Check if expense type code is valid (legacy support)
 */
export function isValidExpenseTypeCode(code: number): boolean {
  return code in EXPENSE_TYPE_CODE_TO_STRING;
}

/**
 * Get array of expense types for dropdowns/selectors
 */
export function getExpenseTypeOptions(): { value: string; label: string; description: string }[] {
  return VALID_EXPENSE_TYPES.map(type => {
    const expenseType = WYDEN_EXPENSE_TYPES[type]!;
    return {
      value: type,
      label: expenseType.name,
      description: expenseType.description
    };
  });
}